import {Injectable} from '@angular/core';
import {Marked} from 'marked';
import {environment} from '../../environments/environment';
import {DEFAULT_CHAT_LINK_HOSTS, allowedChatLinkUrl, sanitizeChatHtml} from './chat-html-sanitizer';
import {ChatSource} from './chat-stream.service';

// Same test as the server's ChatReplyFormatter: a reply that uses any of these tags is HTML.
// Rendering HTML as Markdown would turn indented lines into code blocks.
const HTML_TAG = /<(?:p|ul|ol|li|br|strong|em|b|i|a|code|h[1-6]|div|span|table)\b[^>]*>/i;

// A tag cut off at the end of a streamed chunk ("<", "</", "<p", "<a href=…") would show as text
// until the next chunk arrives. "3 < 4" doesn't match: a tag starts with a letter or "/".
const PARTIAL_TAG_AT_END = /<(?:\/?[a-z][^<>]*|\/)?$/i;

export function looksLikeHtml(text: string): boolean {
  return HTML_TAG.test(text);
}

/**
 * Renders assistant text for display: Markdown (or HTML as is), then the DOMPurify allowlist.
 * Mirrors the server (Markdig CommonMark, no tables, no bare-URL links), so the streamed preview
 * looks like the final server HTML that replaces it.
 */
@Injectable({providedIn: 'root'})
export class MarkdownService {
  readonly linkHosts: readonly string[] = environment.chatLinkHosts?.length ? environment.chatLinkHosts : DEFAULT_CHAT_LINK_HOSTS;

  private readonly marked = new Marked({gfm: true, breaks: false, async: false}).use({
    tokenizer: {
      // No bare-URL autolinks and no tables, like the server's CommonMark pipeline.
      url: () => undefined,
      table: () => undefined,
    },
  });

  /** Streamed or final assistant text to safe HTML. */
  render(text: string | null | undefined): string {
    text = text?.replace(PARTIAL_TAG_AT_END, '');
    if (!text) {
      return '';
    }

    const html = looksLikeHtml(text) ? text : (this.marked.parse(text, {async: false}) as string);
    return sanitizeChatHtml(html, this.linkHosts);
  }

  /** The server's final HTML, sanitized again (defense in depth). */
  sanitize(html: string | null | undefined): string {
    return sanitizeChatHtml(html, this.linkHosts);
  }

  /** Sources the client also allows, with any signature dropped. */
  allowedSources(sources: readonly ChatSource[] | null | undefined): ChatSource[] {
    return (sources ?? []).flatMap(source => {
      const url = allowedChatLinkUrl(source?.url, this.linkHosts);
      return url ? [{title: String(source.title ?? ''), url}] : [];
    });
  }
}

/** Plain text of sanitized HTML, for announcements and copy. Block elements become line breaks. */
export function toPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const block of Array.from(doc.body.querySelectorAll('p, li, br'))) {
    block.append('\n');
  }
  for (const item of Array.from(doc.body.querySelectorAll('li'))) {
    item.prepend('• ');
  }

  return (doc.body.textContent ?? '')
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** A bubble under an answer. With a url it opens in a new tab; otherwise clicking sends its label as the next message. */
export interface ChatBubble {
  label: string;
  url: string | null;
}

const BUBBLES_LABEL = 'next steps';
const MAX_BUBBLES = 6;
const MAX_BUBBLE_LENGTH = 120;

/**
 * Lifts the "Next steps" list the agent ends an answer with (agent-instructions.md, NEXT STEPS) out of
 * sanitized HTML. Only a final <p>Next steps</p><ul> whose items are each one link or plain text
 * qualifies; anything else is left in the answer as it is.
 */
export function takeBubbles(html: string): {html: string; bubbles: ChatBubble[]} {
  const none = {html, bubbles: []};
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const list = doc.body.lastElementChild;
  const label = list?.previousElementSibling;
  if (!list || list.tagName !== 'UL' || label?.tagName !== 'P'
      || (label.textContent ?? '').trim().replace(/:$/, '').toLowerCase() !== BUBBLES_LABEL
      || (list.nextSibling?.textContent ?? '').trim() !== '') {
    return none;
  }

  const bubbles = Array.from(list.children).map(toBubble);
  if (bubbles.length === 0 || bubbles.length > MAX_BUBBLES || bubbles.some(b => b === null)) {
    return none;
  }

  label.remove();
  list.remove();
  return {html: doc.body.innerHTML.trim(), bubbles: bubbles as ChatBubble[]};
}

function toBubble(item: Element): ChatBubble | null {
  const label = (item.textContent ?? '').replace(/\s+/g, ' ').trim();
  const links = item.querySelectorAll('a');
  if (item.tagName !== 'LI' || !label || label.length > MAX_BUBBLE_LENGTH || links.length > 1 || item.querySelector('ul, ol, p')) {
    return null;
  }
  if (links.length === 1) {
    // Links here already passed both sanitizers, so the href is allowed.
    const href = links[0].getAttribute('href');
    return href && (links[0].textContent ?? '').replace(/\s+/g, ' ').trim() === label ? {label, url: href} : null;
  }
  return {label, url: null};
}
